import { useEffect, useRef } from "react";

/**
 * THEOplayerコンテナの初期化とレスポンシブレイアウト管理を行うカスタムフック
 *
 * @description
 * videojsにでは、playerの幅に応じて以下のレイアウトクラスをコンテナに追加/削除する機能があるが、
 * theoplayerでは自動で行われないため、ResizeObserverを使って手動で実装している。
 *
 * - コンテナに必要な基本クラス（video-js, theoplayer-skin, vjs-16-9）を追加
 * - ResizeObserverでコンテナサイズを監視し、レイアウトクラスを動的に更新
 * - THEOplayerが後から追加するクラス（theo-airplay-unavailableなど）は保持される
 *
 * @returns containerRef - THEOplayerコンテナ要素への参照
 */
export const useTheoplayerContainer = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) return;

    // 基本クラスを設定（THEOplayerマウント前に必要なクラス）
    containerElement.classList.add(
      "video-js",
      "theoplayer-container",
      "theoplayer-skin",
      "vjs-16-9"
    );

    const updateLayoutClass = (width: number) => {
      // 既存のレイアウトクラスを削除
      const layoutClasses = [
        "vjs-layout-tiny",
        "vjs-layout-x-small",
        "vjs-layout-small",
        "vjs-layout-medium",
        "vjs-layout-large",
        "vjs-layout-x-large",
        "vjs-layout-huge",
      ];
      layoutClasses.forEach((cls) => containerElement.classList.remove(cls));

      // 新しいレイアウトクラスを追加
      let newClass = "";
      if (width >= 0 && width <= 210) {
        newClass = "vjs-layout-tiny";
      } else if (width >= 211 && width <= 320) {
        newClass = "vjs-layout-x-small";
      } else if (width >= 321 && width <= 425) {
        newClass = "vjs-layout-small";
      } else if (width >= 426 && width <= 768) {
        newClass = "vjs-layout-medium";
      } else if (width >= 769 && width <= 1440) {
        newClass = "vjs-layout-large";
      } else if (width >= 1441 && width <= 2560) {
        newClass = "vjs-layout-x-large";
      } else if (width >= 2561) {
        newClass = "vjs-layout-huge";
      }

      if (newClass) {
        containerElement.classList.add(newClass);
      }
    };

    // ResizeObserverを使ってコンテナのサイズ変化を監視
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        updateLayoutClass(width);
      }
    });

    resizeObserver.observe(containerElement);

    // 初期化時にも実行
    updateLayoutClass(containerElement.clientWidth);

    // クリーンアップ関数
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return containerRef;
};
